/**
 * Cross-browser fullscreen request that no-ops silently when the browser
 * doesn't support the API.
 *
 * Why this exists: `Element.requestFullscreen()` is not implemented on every
 * browser — most notably iOS Safari, which crashes the page with
 * `TypeError: F.requestFullscreen is not a function` when called on a
 * non-video element (Sentry: TODOWITH-AI-FRONTEND-T). This helper:
 *
 *   1. Tries the standard API, then WebKit-prefixed, then MS-prefixed, then
 *      iOS's video-only `webkitEnterFullscreen` (last-ditch — only relevant
 *      when the element is an HTMLVideoElement).
 *   2. Returns silently when nothing exists (iOS Safari on a div), instead of
 *      throwing. The "Fullscreen" button becomes a soft no-op there rather
 *      than crashing the React tree.
 *   3. Swallows the rejected promise some browsers return when called outside
 *      a direct user gesture — that case shouldn't be a hard error either.
 */

type FullscreenLike = {
  requestFullscreen?: () => Promise<void> | void;
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitEnterFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

export function requestFullscreenOn(element: HTMLElement | null): void {
  if (!element) return;
  const el = element as HTMLElement & FullscreenLike;
  const fn =
    el.requestFullscreen ??
    el.webkitRequestFullscreen ??
    el.webkitEnterFullscreen ??
    el.msRequestFullscreen;
  if (typeof fn !== "function") return;
  try {
    const result = fn.call(el);
    if (result && typeof (result as Promise<void>).catch === "function") {
      (result as Promise<void>).catch(() => {
        // Some browsers reject when called outside a user-gesture context.
      });
    }
  } catch {
    // Browser refused fullscreen entirely — silently no-op.
  }
}
